import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';

// Layout components
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { AnnouncementBanner } from '@/components/layout/AnnouncementBanner';
import { ScrollToTop } from '@/components/layout/ScrollToTop';
import { ThirdPartyScripts } from '@/components/layout/ThirdPartyScripts';

// Section components - Hero is kept sync for LCP
import { HeroSection } from '@/components/sections/HeroSection';

// Lazy loaded components
const ClientLogos = lazy(() => import('@/components/sections/ClientLogos').then(module => ({ default: module.ClientLogos })));
const ServicesSection = lazy(() => import('@/components/sections/ServicesSection').then(module => ({ default: module.ServicesSection })));
const WhyHakxcore = lazy(() => import('@/components/sections/WhyHakxcore').then(module => ({ default: module.WhyHakxcore })));
const CaseStudies = lazy(() => import('@/components/sections/CaseStudies').then(module => ({ default: module.CaseStudies })));
const SecurityPortfolio = lazy(() => import('@/components/sections/SecurityPortfolio').then(module => ({ default: module.SecurityPortfolio })));
const Testimonials = lazy(() => import('@/components/sections/Testimonials').then(module => ({ default: module.Testimonials })));
const BlogSection = lazy(() => import('@/components/sections/BlogSection').then(module => ({ default: module.BlogSection })));
const FAQSection = lazy(() => import('@/components/sections/FAQSection').then(module => ({ default: module.FAQSection })));
const ContactCTA = lazy(() => import('@/components/sections/ContactCTA').then(module => ({ default: module.ContactCTA })));

// Pages
const CareersPage = lazy(() => import('@/components/pages/CareersPage').then(module => ({ default: module.CareersPage })));
const TermsPage = lazy(() => import('@/components/pages/TermsPage').then(module => ({ default: module.TermsPage })));
const PrivacyPage = lazy(() => import('@/components/pages/PrivacyPage').then(module => ({ default: module.PrivacyPage })));
const SecurityPage = lazy(() => import('@/components/pages/SecurityPage').then(module => ({ default: module.SecurityPage })));
const NotFoundPage = lazy(() => import('@/components/pages/NotFoundPage').then(module => ({ default: module.NotFoundPage })));

// Loading component
const SectionLoader = () => <div className="h-40 w-full flex items-center justify-center bg-dark-bg/50 animate-pulse rounded-xl" />;

// Reveal animation observer
const useRevealAnimation = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.reveal-up');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
};

// Theme hook
const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first, default to light theme
    const savedTheme = localStorage.getItem('hakxcore-theme');
    return savedTheme || 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('hakxcore-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return { theme, toggleTheme };
};

const HomePage = ({ theme, toggleTheme }) => {
  useRevealAnimation();
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const [bannerHeight, setBannerHeight] = useState(0);

  useEffect(() => {
    if (isBannerVisible) {
      const banner = document.querySelector('.banner-container');
      if (banner) {
        setBannerHeight(banner.offsetHeight);
      }
    } else {
      setBannerHeight(0);
    }
  }, [isBannerVisible]);

  return (
    <main className="min-h-screen">
      <AnnouncementBanner isVisible={isBannerVisible} setIsVisible={setIsBannerVisible} />
      <Navbar theme={theme} toggleTheme={toggleTheme} isBannerVisible={isBannerVisible} bannerHeight={bannerHeight} />
      <HeroSection />
      {/* Near-fold sections: load first */}
      <Suspense fallback={<SectionLoader />}>
        <ClientLogos />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <ServicesSection />
      </Suspense>
      {/* Below-fold sections: independent loading */}
      <Suspense fallback={<SectionLoader />}>
        <WhyHakxcore />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <CaseStudies />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <SecurityPortfolio />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <Testimonials />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <BlogSection />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <FAQSection />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <ContactCTA />
      </Suspense>
      <Footer />
    </main>
  );
};

const InternalPageLayout = ({ children, theme, toggleTheme }) => {
  return (
    <main className="min-h-screen bg-background transition-colors duration-300">
      <Navbar theme={theme} toggleTheme={toggleTheme} isBannerVisible={false} bannerHeight={0} transparent={false} />
      <div className="pt-20">
        {children}
      </div>
      <Footer />
    </main>
  );
};

function App() {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="App overflow-x-hidden">
      <BrowserRouter suppressHydrationWarning>
        <ScrollToTop />
        <ThirdPartyScripts />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-dark-bg text-dark-text">Loading...</div>}>
          <Routes>
            <Route path="/" element={<HomePage theme={theme} toggleTheme={toggleTheme} />} />
            <Route path="/careers" element={<InternalPageLayout theme={theme} toggleTheme={toggleTheme}><CareersPage /></InternalPageLayout>} />
            <Route path="/terms" element={<InternalPageLayout theme={theme} toggleTheme={toggleTheme}><TermsPage /></InternalPageLayout>} />
            <Route path="/privacy" element={<InternalPageLayout theme={theme} toggleTheme={toggleTheme}><PrivacyPage /></InternalPageLayout>} />
            <Route path="/security" element={<InternalPageLayout theme={theme} toggleTheme={toggleTheme}><SecurityPage /></InternalPageLayout>} />

            {/* Legacy Redirects */}
            <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
            <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />

            <Route path="*" element={<InternalPageLayout theme={theme} toggleTheme={toggleTheme}><NotFoundPage /></InternalPageLayout>} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
            border: '1px solid hsl(var(--border))',
          },
        }}
      />
    </div>
  );
}

export default App;
